import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadResume, createOrUpdateSeekerProfile, getSeekerProfile } from '../services/seekerProfileService';
import { getPackagesByType, PACKAGE_TYPES } from '../services/packageService';
import { initiateRazorpayPayment, calculateGST } from '../services/razorpayService';
import { createPaymentRecord, markPaymentSuccess, markPaymentFailed } from '../services/paymentService';
import { createSubscription, getUserActiveSubscription } from '../services/subscriptionService';
import Footer from '../components/Footer';
import ContentWithAds from '../components/ContentWithAds';

const SeekerProfile = ({ user, userData }) => {
  const navigate = useNavigate();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [address, setAddress] = useState('');
  const [mobile, setMobile] = useState('');
  const [referenceMobile, setReferenceMobile] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFileName, setResumeFileName] = useState('');
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Check if user has an active subscription
    const checkSubscriptionAndProfile = async () => {
      try {
        const subResult = await getUserActiveSubscription(user.uid);
        if (subResult.success && subResult.subscription) {
          setHasSubscription(true);
        }

        const result = await getSeekerProfile(user.uid);
        if (result.success && result.profile) {
          const profile = result.profile;
          if (profile.personalInfo) {
            setAddress(profile.personalInfo.address || '');
            setMobile(profile.personalInfo.phone || '');
            setReferenceMobile(profile.personalInfo.referenceMobile || '');
          }
          if (profile.resumeFileName) {
            setResumeFileName(profile.resumeFileName);
          }
        }
      } catch (error) {
        console.error("Error loading profile or subscription:", error);
      }
    };
    checkSubscriptionAndProfile();
  }, [user, navigate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
      setResumeFileName(file.name);
    }
  };

  const handleProfileSubmitAndPay = async (e) => {
    e.preventDefault();
    setIsProcessingPayment(true);
    
    try {
      // 1. Upload Resume if exists
      let resumeURL = '';
      if (resumeFile) {
        const uploadResult = await uploadResume(user.uid, resumeFile);
        if (uploadResult.success) {
          resumeURL = uploadResult.downloadURL;
        } else {
          alert('Failed to upload resume. Proceeding with payment anyway.');
        }
      }

      // 2. Save basic profile data
      const updateData = {
        personalInfo: {
          address,
          phone: mobile,
          referenceMobile,
        }
      };
      
      if (resumeURL) {
        updateData.resumeURL = resumeURL;
        updateData.resumeFileName = resumeFileName;
      }

      await createOrUpdateSeekerProfile(user.uid, updateData);

      if (hasSubscription) {
        alert('Profile updated successfully!');
        setIsProcessingPayment(false);
        navigate('/dashboard');
        return;
      }

      // 3. Handle Payment
      const packagesResult = await getPackagesByType(PACKAGE_TYPES.JOB_SEEKER);
      if (!packagesResult.success || !packagesResult.packages.length) {
        alert("No packages available currently.");
        setIsProcessingPayment(false);
        return;
      }
      
      const pkg = packagesResult.packages.find(p => p.price > 0) || packagesResult.packages[0];
      
      if (!pkg || pkg.price === 0) {
         alert("Paid package not found. Please visit the packages page.");
         setIsProcessingPayment(false);
         return;
      }

      const gstCalculation = calculateGST(pkg.price);
      const totalAmount = gstCalculation.totalAmount;
      const baseAmount = pkg.price;

      const paymentData = {
        userId: user.uid,
        packageId: pkg.id,
        packageName: pkg.name,
        amount: totalAmount,
        currency: 'INR',
        gstAmount: gstCalculation.gstAmount,
        baseAmount: baseAmount,
        totalAmount: totalAmount,
        userEmail: userData?.email || user?.email || '',
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : (user?.displayName || 'Job Seeker'),
        userPhone: mobile || userData?.phone || '',
      };

      const paymentResult = await createPaymentRecord(paymentData);
      if (!paymentResult.success) throw new Error('Failed to create payment record');

      const firebasePaymentId = paymentResult.paymentId;

      await initiateRazorpayPayment({
        amount: totalAmount,
        currency: 'INR',
        packageName: pkg.name,
        packageId: pkg.id,
        userEmail: paymentData.userEmail,
        userName: paymentData.userName,
        userPhone: paymentData.userPhone,
        userId: user.uid,
        onSuccess: async (paymentResponse) => {
          await markPaymentSuccess(firebasePaymentId, paymentResponse);
          
          const subscriptionData = {
            userId: user.uid,
            packageId: pkg.id,
            packageName: pkg.name,
            packageType: pkg.type,
            price: pkg.price,
            duration: pkg.duration,
            features: pkg.features || [],
            maxApplications: pkg.maxApplications || 0,
            resumeTemplates: pkg.resumeTemplates || 0,
            profileBoost: pkg.profileBoost || false,
            userType: 'jobseeker',
            paymentId: firebasePaymentId,
            razorpayPaymentId: paymentResponse.paymentId,
            razorpayOrderId: paymentResponse.orderId || null,
          };

          const subscriptionResult = await createSubscription(subscriptionData);
          if (subscriptionResult.success) {
            alert(`✅ Profile completed and successfully purchased ${pkg.name}!`);
            navigate('/dashboard');
          } else {
            alert('Payment successful but failed to activate subscription. Please contact support.');
          }
          setIsProcessingPayment(false);
        },
        onFailure: async (error) => {
          await markPaymentFailed(firebasePaymentId, error.error || 'Payment failed');
          if (!error.cancelled) alert('Payment failed: ' + (error.error || 'Unknown error'));
          setIsProcessingPayment(false);
        },
      });
      
    } catch (error) {
      console.error('Payment Error:', error);
      alert('Error initiating payment: ' + error.message);
      setIsProcessingPayment(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
        <ContentWithAds>
          <div className="max-w-xl mx-auto px-4 sm:px-6 mb-6 flex justify-start">
            <button
              onClick={() => navigate('/dashboard')}
              className="group flex items-center text-gray-600 hover:text-primary-700 transition-all duration-200 font-semibold text-sm bg-white/80 hover:bg-white px-5 py-2.5 rounded-xl shadow-sm hover:shadow border border-gray-200/80 backdrop-blur-sm"
            >
              <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
          </div>
          <div className="max-w-xl mx-auto px-4 sm:px-6">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden relative border border-gray-100">
              <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-primary-600 to-primary-800 opacity-10"></div>
              
              <div className="p-8 relative z-10">
                <div className="mb-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-tr from-primary-600 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg transform -rotate-3 hover:rotate-0 transition-transform duration-300">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                    {hasSubscription ? 'Update Your Profile' : 'Complete Your Profile'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
                    {hasSubscription 
                      ? 'Update your address, contact numbers, and resume below.' 
                      : 'Take a moment to provide these details to unlock your full potential on our platform.'}
                  </p>
                </div>
                
                <form className="space-y-5" onSubmit={handleProfileSubmitAndPay}>
                  {/* Address */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Address</label>
                    <div className="relative">
                      <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      </div>
                      <textarea required value={address} onChange={e => setAddress(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-all duration-200 resize-none" rows="2" placeholder="Enter your full address"></textarea>
                    </div>
                  </div>
                  
                  {/* Mobile Numbers - Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mobile Number</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                        </div>
                        <input required value={mobile} onChange={e => setMobile(e.target.value.replace(/[^0-9]/g, ''))} type="tel" pattern="[0-9]{10}" maxLength="10" title="Please enter exactly 10 digits" className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-all duration-200" placeholder="10-digit mobile no." />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Reference Mobile</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                        </div>
                        <input value={referenceMobile} onChange={e => setReferenceMobile(e.target.value.replace(/[^0-9]/g, ''))} type="tel" pattern="[0-9]{10}" maxLength="10" title="Please enter exactly 10 digits" className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-all duration-200" placeholder="Optional 10-digit no." />
                      </div>
                    </div>
                  </div>
                  
                  {/* Resume Upload - Custom styled */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Resume Upload</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors duration-200 group cursor-pointer relative overflow-hidden">
                      <input id="file-upload" name="file-upload" type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept=".pdf,.doc,.docx" onChange={handleFileChange} />
                      <div className="space-y-1 text-center pointer-events-none">
                        <svg className="mx-auto h-12 w-12 text-gray-400 group-hover:text-primary-500 transition-colors" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600 justify-center">
                          <span className="relative font-medium text-primary-600 focus-within:outline-none px-1" id="file-upload-display">
                            {resumeFileName ? resumeFileName : <>Upload a file <span className="text-gray-500 font-normal">or drag and drop</span></>}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Submit Button */}
                  <div className="pt-2">
                    <button type="submit" disabled={isProcessingPayment} className={`w-full py-3.5 px-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform flex justify-center items-center gap-2 ${isProcessingPayment ? 'opacity-70 cursor-not-allowed scale-100' : 'hover:-translate-y-1'}`}>
                      <span>
                        {isProcessingPayment 
                          ? (hasSubscription ? 'Updating Profile...' : 'Processing Payment...') 
                          : (hasSubscription ? 'Update Profile' : 'Submit & Continue')}
                      </span>
                      {!isProcessingPayment && <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </ContentWithAds>
      </div>
      <div className="hidden lg:block">
        <Footer />
      </div>
    </>
  );
};

export default SeekerProfile;